import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getFirestore, collection, getDocs, addDoc, deleteDoc, doc, updateDoc } from "firebase/firestore";
import jsPDF from 'jspdf';
import app from "./firebase";
import styles from "./InventoryDashboard.module.css";

const db = getFirestore(app);

// Standardized units for dropdown selection
const STANDARD_UNITS = [
  "kg", "g", "lbs", "oz", "units", "pieces", "boxes", "packets", 
  "liters", "ml", "cups", "tbsp", "tsp", "bottles", "cans", "bags"
];

export default function InventoryDashboard() {
  const location = useLocation();
  const [ingredients, setIngredients] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportFormat, setReportFormat] = useState("pdf");
  const [showFormatModal, setShowFormatModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all"); // "all", "low", "out"
  const [showEditStock, setShowEditStock] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [stockForm, setStockForm] = useState({ currentStock: 0, minStock: 0, unit: "" });
  const [showBulkRestock, setShowBulkRestock] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [bulkRestockAmounts, setBulkRestockAmounts] = useState({});
  const [highlightedId, setHighlightedId] = useState(null);
  const [purchaseDetails, setPurchaseDetails] = useState({
    date: new Date().toISOString().split("T")[0],
    packageCount: "",
    packageSize: "",
    packageUnit: "lbs",
    costPerPackage: "",
    pricePerLb: "",
    purchaseStoreId: ""
  });
  
  // Ingredient CRUD states
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editIngredient, setEditIngredient] = useState(null);
  const [form, setForm] = useState({ 
    name: "", 
    store: "", 
    currentStock: 0, 
    minStock: 5, 
    unit: "units", 
    pricePerLb: ""
  });
  const [storeSearch, setStoreSearch] = useState("");

  const parseNumber = (value) => {
    if (value === undefined || value === null || value === "") return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };

  const isWaterName = (name) => String(name || "").trim().toLowerCase() === "water";

  const formatStockForDisplay = (value) => {
    if (value === Number.POSITIVE_INFINITY || value === Infinity) return "∞";
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric.toFixed(2) : "0.00";
  };

  const toJsDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    // Firestore Timestamp
    if (typeof value === "object" && typeof value.seconds === "number") {
      return new Date(value.seconds * 1000);
    }
    if (typeof value === "string") {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  };

  const formatCurrency = (value) => {
    const num = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(num)) return "-";
    return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(num);
  };

  const formatDate = (value) => {
    const date = toJsDate(value);
    return date ? date.toLocaleDateString() : "-";
  };

  const convertQuantity = (value, fromUnit, toUnit) => {
    if (value === null || value === undefined) return null;
    const normalizedFrom = fromUnit?.toLowerCase();
    const normalizedTo = toUnit?.toLowerCase();
    if (!normalizedFrom || !normalizedTo) return null;

    if (normalizedFrom === normalizedTo) return value;

    // Convert to pounds as an intermediate where possible
    const toPounds = (val, unit) => {
      switch (unit) {
        case "lb":
        case "lbs":
          return val;
        case "kg":
          return val * 2.20462;
        case "g":
          return val / 453.592;
        case "oz":
          return val / 16;
        default:
          return null;
      }
    };

    const fromInPounds = toPounds(value, normalizedFrom);
    if (fromInPounds === null) return null;

    const fromPoundsToTarget = (val, unit) => {
      switch (unit) {
        case "lb":
        case "lbs":
          return val;
        case "kg":
          return val / 2.20;
        case "g":
          return val * 453.59;
        case "oz":
          return val * 16;
        default:
          return null;
      }
    };

    return fromPoundsToTarget(fromInPounds, normalizedTo);
  };

  const convertCostToPricePerLb = (unit, costPerUnit) => {
    if (costPerUnit === null || costPerUnit === undefined || costPerUnit === "") return null;
    const normalized = unit?.toLowerCase();
    switch (normalized) {
      case "kg":
        return costPerUnit / 2.20462;
      case "g":
        return costPerUnit * 453.592;
      case "oz":
        return costPerUnit * 16;
      case "lb":
      case "lbs":
        return costPerUnit;
      default:
        return null;
    }
  };

  const resetPurchaseDetails = (purchaseStoreId = "") => {
    setPurchaseDetails({
      date: new Date().toISOString().split("T")[0],
      packageCount: "",
      packageSize: "",
      packageUnit: "lbs",
      costPerPackage: "",
      pricePerLb: "",
      purchaseStoreId
    });
  };

  const logIngredientPurchaseHistory = async ({
    ingredient,
    totalQuantity,
    quantityChange,
    costPerUnit,
    totalPrice,
    pricePerLb,
    packageCount,
    packageSize,
    packageUnit,
    costPerPackage,
    purchaseStoreId,
    date
  }) => {
    if (!ingredient?.id) return;
    const resolvedStoreId = purchaseStoreId || ingredient.storeId || null;
    const storeName = stores.find(s => s.id === resolvedStoreId)?.name || ingredient.storeName || null;

    try {
      const purchaseDate = date ? new Date(date) : new Date();

      const historyRef = await addDoc(collection(db, "ingredient_purchase_history"), {
        ingredientId: ingredient.id,
        ingredientName: ingredient.name,
        storeId: resolvedStoreId,
        storeName,
        unit: ingredient.unit || null,
        quantity: quantityChange ?? null,
        totalQuantity: totalQuantity ?? null,
        costPerBaseUnit: costPerUnit ?? null,
        totalPrice: totalPrice ?? null,
        pricePerLb: pricePerLb ?? null,
        packageCount: packageCount ?? null,
        packageSize: packageSize ?? null,
        packageUnit: packageUnit || null,
        costPerPackage: costPerPackage ?? null,
        purchaseStoreId: resolvedStoreId,
        date: purchaseDate,
        createdAt: new Date()
      });

      // Create FIFO-consumable inventory lot for positive restocks
      const qty = parseNumber(quantityChange);
      if (qty !== null && qty > 0) {
        await addDoc(collection(db, "ingredient_inventory_lots"), {
          ingredientId: ingredient.id,
          ingredientName: ingredient.name,
          unit: ingredient.unit || null,
          storeId: resolvedStoreId,
          storeName,
          purchaseHistoryId: historyRef.id,
          purchaseDate,
          quantityPurchased: qty,
          remainingQuantity: qty,
          costPerBaseUnit: costPerUnit ?? null,
          totalPrice: totalPrice ?? null,
          pricePerLb: pricePerLb ?? null,
          packageCount: packageCount ?? null,
          packageSize: packageSize ?? null,
          packageUnit: packageUnit || null,
          costPerPackage: costPerPackage ?? null,
          createdAt: new Date(),
          updatedAt: new Date(),
          isLegacy: false
        });
      }
    } catch (err) {
      console.error("Error logging ingredient purchase history:", err);
    }
  };

  // Fetch ingredients and stores
  const fetchData = async () => {
    setLoading(true);
    try {
      const [ingSnap, storeSnap] = await Promise.all([
        getDocs(collection(db, "ingredients")),
        getDocs(collection(db, "stores")),
      ]);
      
      const storesArr = storeSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setStores(storesArr);

      const ingredientsArr = ingSnap.docs.map(doc => {
        const data = doc.data();
        const isWater = String(data.name || "").trim().toLowerCase() === "water";
        return {
          id: doc.id,
          ...data,
          currentStock: isWater ? Number.POSITIVE_INFINITY : (data.currentStock || 0),
          minStock: data.minStock || 5,
          unit: data.unit || "units",
          pricePerLb: data.pricePerLb ?? null,
          lastUpdated: data.lastUpdated || null,
          storeName: storesArr.find(s => s.id === data.storeId)?.name || "Unknown"
        };
      });
      setIngredients(ingredientsArr);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Handle highlighting from search - triggers on any highlight event
  useEffect(() => {
    if (location.state?.highlightId && !loading && ingredients.length > 0) {
      setHighlightedId(location.state.highlightId);
      
      const scrollToElement = () => {
        const element = document.getElementById(`ingredient-${location.state.highlightId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return true;
        }
        return false;
      };

      // Try immediately first
      if (!scrollToElement()) {
        // If element not found, wait a bit for DOM to update
        const timer = setTimeout(() => {
          scrollToElement();
        }, 100);
        setTimeout(() => setHighlightedId(null), 3000);
        return () => clearTimeout(timer);
      } else {
        setTimeout(() => setHighlightedId(null), 3000);
      }
    }
  }, [location.state, loading, ingredients]);

  // Add new ingredient
  const addIngredient = async () => {
    if (!form.name || !form.store) return;
    try {
      const docRef = await addDoc(collection(db, "ingredients"), {
        name: form.name,
        storeId: form.store,
        currentStock: Number(form.currentStock),
        minStock: Number(form.minStock),
        unit: form.unit,
        lastUpdated: new Date().toISOString()
      });
      const storeName = stores.find(s => s.id === form.store)?.name || "Unknown";
      await logIngredientPurchaseHistory({
        ingredient: {
          id: docRef.id,
          name: form.name,
          storeId: form.store,
          storeName,
          unit: form.unit
        },
        totalQuantity: Number(form.currentStock),
        quantityChange: Number(form.currentStock),
        costPerUnit: null,
        totalPrice: null,
        pricePerLb: convertCostToPricePerLb(form.unit, null),
        packageCount: null,
        packageSize: null,
        packageUnit: null,
        costPerPackage: null,
        purchaseStoreId: form.store,
        date: new Date()
      });
      setForm({ name: "", store: "", currentStock: 0, minStock: 5, unit: "units", pricePerLb: "" });
      setShowAdd(false);
      fetchData();
    } catch (err) {
      console.error("Error adding ingredient:", err);
    }
  };

  // Update ingredient
  const updateIngredient = async () => {
    if (!form.name || !form.store) return;
    try {
      const newStock = parseNumber(form.currentStock);
      const newMinStock = parseNumber(form.minStock) ?? 0;
      const quantityChange = newStock !== null && editIngredient ? newStock - (editIngredient.currentStock || 0) : null;
      const pricePerLbValue = parseNumber(form.pricePerLb);

      await updateDoc(doc(db, "ingredients", editIngredient.id), {
        name: form.name,
        storeId: form.store,
        currentStock: newStock ?? 0,
        minStock: newMinStock,
        unit: form.unit,
        pricePerLb: pricePerLbValue,
        lastUpdated: new Date().toISOString()
      });
      await logIngredientPurchaseHistory({
        ingredient: {
          ...editIngredient,
          name: form.name,
          storeId: form.store,
          storeName: stores.find(s => s.id === form.store)?.name || editIngredient?.storeName,
          unit: form.unit
        },
        totalQuantity: newStock ?? null,
        quantityChange,
        costPerUnit: null,
        totalPrice: null,
        pricePerLb: pricePerLbValue,
        packageCount: null,
        packageSize: null,
        packageUnit: null,
        costPerPackage: null,
        purchaseStoreId: form.store,
        date: new Date()
      });
      setForm({ name: "", store: "", currentStock: 0, minStock: 5, unit: "units", pricePerLb: "" });
      setShowEdit(false);
      setEditIngredient(null);
      fetchData();
    } catch (err) {
      console.error("Error updating ingredient:", err);
    }
  };

  // Delete ingredient
  const deleteIngredient = async (id) => {
    if (window.confirm("Are you sure you want to delete this ingredient?")) {
      try {
        await deleteDoc(doc(db, "ingredients", id));
        fetchData();
      } catch (err) {
        console.error("Error deleting ingredient:", err);
      }
    }
  };

  // Get low stock and out of stock items
  const getLowStockItems = () => {
    return ingredients.filter(ingredient => {
      if (isWaterName(ingredient.name)) return false;
      return ingredient.currentStock <= ingredient.minStock;
    });
  };

  const getOutOfStockItems = () => {
    return ingredients.filter(ingredient => {
      if (isWaterName(ingredient.name)) return false;
      return ingredient.currentStock === 0;
    });
  };

  // Group items by store for shopping list
  const groupItemsByStore = (items) => {
    const grouped = {};
    items.forEach(item => {
      const storeName = item.storeName || 'Unknown Store';
      if (!grouped[storeName]) {
        grouped[storeName] = [];
      }
      grouped[storeName].push(item);
    });
    return grouped;
  };

  // Generate PDF shopping list
  const generateShoppingListPDF = () => {
    const lowStockItems = getLowStockItems();
    const outOfStockItems = getOutOfStockItems();
    
    if (lowStockItems.length === 0 && outOfStockItems.length === 0) {
      alert('No items need restocking!');
      return;
    }

    const doc = new jsPDF();
    let yPosition = 20;
    
    // Title
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('Shopping List - Inventory Restock', 20, yPosition);
    yPosition += 10;
    
    // Date
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, yPosition);
    yPosition += 20;

    // Out of Stock Items (Priority)
    if (outOfStockItems.length > 0) {
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(220, 38, 38); // Red color
      doc.text('URGENT - Out of Stock Items', 20, yPosition);
      yPosition += 10;
      
      const outOfStockByStore = groupItemsByStore(outOfStockItems);
      
      Object.entries(outOfStockByStore).forEach(([storeName, items]) => {
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(0, 0, 0); // Black color
        doc.text(`${storeName}:`, 25, yPosition);
        yPosition += 8;
        
        doc.setFontSize(12);
        doc.setFont(undefined, 'normal');
        items.forEach(item => {
          const text = `• ${item.name} (Currently: ${formatStockForDisplay(item.currentStock)} ${item.unit})`;
          doc.text(text, 30, yPosition);
          yPosition += 6;
        });
        yPosition += 5;
      });
      yPosition += 10;
    }

    // Low Stock Items
    const lowStockOnly = lowStockItems.filter(item => item.currentStock > 0);
    if (lowStockOnly.length > 0) {
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(245, 158, 11); // Orange color
      doc.text('Low Stock Items', 20, yPosition);
      yPosition += 10;
      
      const lowStockByStore = groupItemsByStore(lowStockOnly);
      
      Object.entries(lowStockByStore).forEach(([storeName, items]) => {
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(0, 0, 0); // Black color
        doc.text(`${storeName}:`, 25, yPosition);
        yPosition += 8;
        
        doc.setFontSize(12);
        doc.setFont(undefined, 'normal');
        items.forEach(item => {
          const text = `• ${item.name} (Currently: ${formatStockForDisplay(item.currentStock)}/${formatStockForDisplay(item.minStock)} ${item.unit})`;
          doc.text(text, 30, yPosition);
          yPosition += 6;
        });
        yPosition += 5;
      });
    }

    // Summary
    yPosition += 10;
    doc.setFontSize(12);
    doc.setFont(undefined, 'italic');
    doc.setTextColor(100, 100, 100);
    doc.text(`Total items to restock: ${lowStockItems.length}`, 20, yPosition);
    yPosition += 6;
    doc.text(`Out of stock: ${outOfStockItems.length} | Low stock: ${lowStockOnly.length}`, 20, yPosition);

    // Save the PDF
    const fileName = `shopping-list-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  const prepareIngredientStatusReportData = async () => {
    const historySnap = await getDocs(collection(db, "ingredient_purchase_history"));
    const historyRows = historySnap.docs.map(d => ({ id: d.id, ...d.data() }));

    // Track most recent purchase rows for each ingredient
    const lastPurchaseByIngredientId = new Map();
    historyRows.forEach(r => {
      const ingredientId = r.ingredientId;
      if (!ingredientId) return;
      const date = toJsDate(r.date || r.createdAt);
      if (!date) return;

      const hasPrice = parseNumber(r.pricePerLb) !== null || parseNumber(r.costPerBaseUnit) !== null;
      const existing = lastPurchaseByIngredientId.get(ingredientId) || {
        latestDate: null,
        latestRow: null,
        pricedDate: null,
        pricedRow: null
      };

      if (!existing.latestDate || date.getTime() > existing.latestDate.getTime()) {
        existing.latestDate = date;
        existing.latestRow = r;
      }

      if (hasPrice && (!existing.pricedDate || date.getTime() > existing.pricedDate.getTime())) {
        existing.pricedDate = date;
        existing.pricedRow = r;
      }

      lastPurchaseByIngredientId.set(ingredientId, existing);
    });

    const sortedIngredients = [...ingredients]
      .filter(ing => (ing.name || "").toLowerCase() !== "water")
      .sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));

    const reportRows = sortedIngredients.map(ing => {
      const currentStock = parseNumber(ing.currentStock) ?? 0;
      const formattedStock = currentStock.toFixed(2);
      const unit = ing.unit || "";

      const last = lastPurchaseByIngredientId.get(ing.id) || null;
      const lastRowWithPrice = last?.pricedRow || last?.latestRow || null;
      const lastBoughtDate = last?.latestDate || null;

      const lastPricePerLb = (() => {
        const direct = parseNumber(lastRowWithPrice?.pricePerLb);
        if (direct !== null) return direct;
        const costPerBaseUnit = parseNumber(lastRowWithPrice?.costPerBaseUnit);
        if (costPerBaseUnit === null) return null;
        return convertCostToPricePerLb(unit, costPerBaseUnit);
      })();

      const resolvedPricePerLb = lastPricePerLb !== null ? lastPricePerLb : parseNumber(ing.pricePerLb);

      const totalPrice = (() => {
        const costPerBaseUnit = parseNumber(lastRowWithPrice?.costPerBaseUnit);
        if (costPerBaseUnit !== null) return currentStock * costPerBaseUnit;

        if (resolvedPricePerLb === null) return null;
        const stockInLb = convertQuantity(currentStock, unit, "lbs");
        if (stockInLb === null) return null;
        return stockInLb * resolvedPricePerLb;
      })();

      return [
        ing.name || "-",
        `${formattedStock}${unit ? ` ${unit}` : ""}`,
        resolvedPricePerLb !== null ? `${formatCurrency(resolvedPricePerLb)} / lb` : "-",
        formatCurrency(totalPrice),
        formatDate(lastBoughtDate)
      ];
    });

    const headers = [
      "Ingredient Name",
      "Current total quantity",
      "Last Price/lb",
      "Total price (based on last purchase)",
      "Date last bought"
    ];

    return { headers, reportRows };
  };

  const downloadIngredientStatusReportPDF = ({ headers, reportRows }) => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const marginX = 10;
    const marginY = 10;
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.setFontSize(16);
    doc.setFont(undefined, "bold");
    doc.text("Ingredient Status Report", marginX, 16);

    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, marginX, 22);

    const colWidths = [85, 55, 40, 55, 35];
    const tableStartX = marginX;
    let y = 28;

    const drawRow = (cells, startY, isHeader = false) => {
      const lineHeight = 4.2;
      const paddingX = 1.5;
      const paddingY = 3.5;

      const cellLines = cells.map((text, idx) => {
        const value = text === null || text === undefined ? "" : String(text);
        return doc.splitTextToSize(value, colWidths[idx] - paddingX * 2);
      });
      const maxLines = Math.max(1, ...cellLines.map(lines => lines.length));
      const rowHeight = paddingY + maxLines * lineHeight + 1.5;

      let x = tableStartX;
      doc.setFont(undefined, isHeader ? "bold" : "normal");

      for (let i = 0; i < cells.length; i++) {
        doc.rect(x, startY, colWidths[i], rowHeight);
        const lines = cellLines[i];
        const textY = startY + paddingY;
        doc.text(lines, x + paddingX, textY);
        x += colWidths[i];
      }
      return rowHeight;
    };

    // Header row
    y += drawRow(headers, y, true);

    // Data rows with paging
    doc.setFontSize(9);
    reportRows.forEach(row => {
      if (y + 12 > pageHeight - marginY) {
        doc.addPage();
        y = marginY;
        doc.setFontSize(9);
        y += drawRow(headers, y, true);
      }

      const height = drawRow(row, y, false);
      y += height;
      if (y > pageHeight - marginY) {
        doc.addPage();
        y = marginY;
        doc.setFontSize(9);
        y += drawRow(headers, y, true);
      }
    });

    const fileName = `ingredient-status-report-${new Date().toISOString().split("T")[0]}.pdf`;
    doc.save(fileName);
  };

  const downloadIngredientStatusReportXLS = ({ headers, reportRows }) => {
    const escapeCell = (value) => {
      if (value === null || value === undefined) return "";
      return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    };

    const headerHtml = `<tr>${headers.map(h => `<th>${escapeCell(h)}</th>`).join("")}</tr>`;
    const rowsHtml = reportRows
      .map(row => `<tr>${row.map(cell => `<td>${escapeCell(cell)}</td>`).join("")}</tr>`)
      .join("");

    const tableHtml = `<!DOCTYPE html><html><head><meta charset="UTF-8"></head><body><table>${headerHtml}${rowsHtml}</table></body></html>`;
    const blob = new Blob(["\ufeff" + tableHtml], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `ingredient-status-report-${new Date().toISOString().split("T")[0]}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleGenerateIngredientStatusReport = async (formatOverride) => {
    if (generatingReport) return;
    if (!ingredients || ingredients.length === 0) {
      alert("No ingredients found.");
      return;
    }

    const format = (formatOverride || reportFormat)?.toLowerCase();
    if (format !== "pdf" && format !== "xls") {
      alert('Please select PDF or XLS before generating.');
      return;
    }

    setGeneratingReport(true);
    try {
      const data = await prepareIngredientStatusReportData();
      if (format === "pdf") {
        downloadIngredientStatusReportPDF(data);
      } else {
        downloadIngredientStatusReportXLS(data);
      }
    } catch (err) {
      console.error("Error generating ingredient status report:", err);
      alert("Error generating report. Please try again.");
    } finally {
      setGeneratingReport(false);
    }
  };

  // Filter ingredients based on search and filter type
  const filteredIngredients = ingredients.filter(ingredient => {
    const matchesSearch = ingredient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ingredient.storeName.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    switch (filterType) {
      case "low":
        return !isWaterName(ingredient.name) && ingredient.currentStock <= ingredient.minStock && ingredient.currentStock > 0;
      case "out":
        return !isWaterName(ingredient.name) && ingredient.currentStock === 0;
      case "instock":
        return ingredient.currentStock > ingredient.minStock;
      case "all":
      default:
        return true;
    }
  });

  // Get stock status for an ingredient
  const getStockStatus = (ingredient) => {
    if (ingredient.currentStock === 0) return "out";
    if (ingredient.currentStock <= ingredient.minStock) return "low";
    return "normal";
  };

  // Get stock status color
  const getStatusColor = (status) => {
    switch (status) {
      case "out": return "#ef4444"; // red
      case "low": return "#f59e0b"; // orange
      case "normal": return "#10b981"; // green
      default: return "#6b7280"; // gray
    }
  };

  // Handle stock update
  const handleStockUpdate = (ingredient) => {
    if (isWaterName(ingredient.name)) {
      alert("Water stock is unlimited and not tracked.");
      return;
    }
    setEditingIngredient(ingredient);
    setStockForm({
      currentStock: Number.isFinite(Number(ingredient.currentStock)) ? ingredient.currentStock : 0,
      minStock: ingredient.minStock,
      unit: ingredient.unit
    });
    resetPurchaseDetails(ingredient.storeId || "");
    setShowEditStock(true);
  };

  // Submit stock update
  const handleStockSubmit = async (e) => {
    e.preventDefault();
    if (!editingIngredient) return;

    try {
      const newMinStock = parseNumber(stockForm.minStock) ?? 0;
      const prevStock = editingIngredient.currentStock || 0;
      const packageCount = parseNumber(purchaseDetails.packageCount);
      const packageSize = parseNumber(purchaseDetails.packageSize);
      const packageUnit = purchaseDetails.packageUnit;
      const costPerPackage = parseNumber(purchaseDetails.costPerPackage);
      const pricePerLbOverride = parseNumber(purchaseDetails.pricePerLb);
      const purchaseStoreId = purchaseDetails.purchaseStoreId || editingIngredient.storeId || "";

      if (packageCount === null || packageSize === null || costPerPackage === null) {
        alert("Packages are required. Please enter: packages purchased, package size, and cost per package.");
        return;
      }

      const quantityChange = convertQuantity(
        packageCount * packageSize,
        packageUnit,
        stockForm.unit || editingIngredient.unit
      );

      if (quantityChange === null) {
        alert(
          `Cannot convert ${packageUnit} to ${stockForm.unit || editingIngredient.unit}. ` +
          "Set Package Unit to match the ingredient unit, or use a convertible weight unit (kg/g/oz/lbs)."
        );
        return;
      }

      const newStock = prevStock + quantityChange;
      const totalLb = convertQuantity(packageCount * packageSize, packageUnit, "lbs");
      const baseTotalPrice = packageCount * costPerPackage;
      const computedPricePerLb = pricePerLbOverride !== null
        ? pricePerLbOverride
        : (totalLb !== null ? baseTotalPrice / totalLb : null);
      const totalPrice = pricePerLbOverride !== null && totalLb !== null
        ? pricePerLbOverride * totalLb
        : baseTotalPrice;
      const costPerBaseUnit = totalPrice / quantityChange;
      const purchaseDate = purchaseDetails.date ? new Date(purchaseDetails.date) : new Date();

      await updateDoc(doc(db, "ingredients", editingIngredient.id), {
        currentStock: newStock,
        minStock: newMinStock,
        unit: stockForm.unit,
        lastUpdated: new Date()
      });

      await logIngredientPurchaseHistory({
        ingredient: editingIngredient,
        totalQuantity: newStock,
        quantityChange,
        costPerUnit: costPerBaseUnit,
        totalPrice,
        pricePerLb: computedPricePerLb,
        packageCount,
        packageSize,
        packageUnit,
        costPerPackage,
        purchaseStoreId,
        date: purchaseDate
      });
      
      setShowEditStock(false);
      setEditingIngredient(null);
      setStockForm({ currentStock: 0, minStock: 0, unit: "" });
      resetPurchaseDetails();
      fetchData();
    } catch (err) {
      console.error("Error updating stock:", err);
    }
  };

  // Handle bulk restock functionality
  const handleItemSelection = (ingredientId) => {
    const newSelectedItems = new Set(selectedItems);
    if (newSelectedItems.has(ingredientId)) {
      newSelectedItems.delete(ingredientId);
      const newAmounts = { ...bulkRestockAmounts };
      delete newAmounts[ingredientId];
      setBulkRestockAmounts(newAmounts);
    } else {
      newSelectedItems.add(ingredientId);
    }
    setSelectedItems(newSelectedItems);
  };

  const handleBulkRestockAmountChange = (ingredientId, amount) => {
    setBulkRestockAmounts({
      ...bulkRestockAmounts,
      [ingredientId]: parseInt(amount) || 0
    });
  };

  const handleBulkRestockSubmit = async () => {
    if (selectedItems.size === 0) {
      alert('Please select at least one item to restock.');
      return;
    }

    try {
      const updatePromises = Array.from(selectedItems).map(async (ingredientId) => {
        const ingredient = ingredients.find(ing => ing.id === ingredientId);
        const restockAmount = bulkRestockAmounts[ingredientId] || 0;
        
        if (restockAmount > 0) {
          const newStock = ingredient.currentStock + restockAmount;
          await updateDoc(doc(db, "ingredients", ingredientId), {
            currentStock: newStock,
            lastUpdated: new Date()
          });
          await logIngredientPurchaseHistory({
            ingredient,
            totalQuantity: newStock,
            quantityChange: restockAmount,
            costPerUnit: null,
            totalPrice: null,
            pricePerLb: convertCostToPricePerLb(ingredient.unit, null),
            purchaseStoreId: ingredient.storeId || null,
            date: new Date()
          });
        }
      });

      await Promise.all(updatePromises);
      
      // Reset bulk restock state
      setShowBulkRestock(false);
      setSelectedItems(new Set());
      setBulkRestockAmounts({});
      fetchData();
      
      alert(`Successfully restocked ${selectedItems.size} items!`);
    } catch (err) {
      console.error("Error during bulk restock:", err);
      alert('Error during bulk restock. Please try again.');
    }
  };

  const selectAllLowStock = () => {
    const lowStockIds = getLowStockItems().map(item => item.id);
    setSelectedItems(new Set(lowStockIds));
  };

  const clearSelection = () => {
    setSelectedItems(new Set());
    setBulkRestockAmounts({});
  };

  // Calculate summary stats
  const stats = {
    total: ingredients.length,
    lowStock: ingredients.filter(ing => !isWaterName(ing.name) && ing.currentStock <= ing.minStock && ing.currentStock > 0).length,
    outOfStock: ingredients.filter(ing => !isWaterName(ing.name) && ing.currentStock === 0).length,
    inStock: ingredients.filter(ing => !isWaterName(ing.name) && ing.currentStock > ing.minStock).length
  };

  const parsedPackageCount = parseNumber(purchaseDetails.packageCount);
  const parsedPackageSize = parseNumber(purchaseDetails.packageSize);
  const parsedCostPerPackage = parseNumber(purchaseDetails.costPerPackage);
  const parsedPricePerLb = parseNumber(purchaseDetails.pricePerLb);
  const packageQuantityBase = parsedPackageCount !== null && parsedPackageSize !== null
    ? convertQuantity(parsedPackageCount * parsedPackageSize, purchaseDetails.packageUnit, editingIngredient?.unit || stockForm.unit)
    : null;
  const packageTotalPrice = parsedPackageCount !== null && parsedCostPerPackage !== null
    ? parsedPackageCount * parsedCostPerPackage
    : null;
  const totalLbForPackages = parsedPackageCount !== null && parsedPackageSize !== null
    ? convertQuantity(parsedPackageCount * parsedPackageSize, purchaseDetails.packageUnit, "lbs")
    : null;
  const computedTotalPriceWithPackages = parsedPricePerLb !== null && totalLbForPackages !== null
    ? parsedPricePerLb * totalLbForPackages
    : packageTotalPrice;
  const suggestedPricePerLb = parsedPricePerLb !== null
    ? parsedPricePerLb
    : (computedTotalPriceWithPackages !== null && totalLbForPackages !== null
      ? computedTotalPriceWithPackages / totalLbForPackages
      : null);

  if (loading) return <p>Loading inventory...</p>;

  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.header}>
        <h2 className={styles.dashboardTitle}>Ingredients Management</h2>
        <button
          className={styles.addButton}
          onClick={() => setShowAdd(true)}
        >
          + Add Ingredient
        </button>
      </div>
      
      {/* Summary Statistics */}
      <div className={styles.statsContainer}>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>{stats.total}</div>
          <div className={styles.statLabel}>Total Items</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber} style={{ color: "#10b981" }}>{stats.inStock}</div>
          <div className={styles.statLabel}>In Stock</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber} style={{ color: "#f59e0b" }}>{stats.lowStock}</div>
          <div className={styles.statLabel}>Low Stock</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber} style={{ color: "#ef4444" }}>{stats.outOfStock}</div>
          <div className={styles.statLabel}>Out of Stock</div>
        </div>
      </div>

      {/* Controls */}
      <div className={styles.topControls}>
        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder="Search ingredients or stores..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        <div className={styles.filterContainer}>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Items</option>
            <option value="instock">In Stock</option>
            <option value="low">Low Stock</option>
            <option value="out">Out of Stock</option>
          </select>
        </div>
        {getLowStockItems().length > 0 && (
          <button
            className={styles.shoppingListButton}
            onClick={generateShoppingListPDF}
          >
            📋 Generate Shopping List PDF ({getLowStockItems().length})
          </button>
        )}
        <button
          className={styles.shoppingListButton}
          onClick={() => {
            if (!generatingReport) setShowFormatModal(true);
          }}
          disabled={generatingReport}
          title={`Generate ingredient status report as ${reportFormat.toUpperCase()}`}
        >
          {generatingReport ? "Generating..." : "Generate Report"}
        </button>
        <button
          className={styles.bulkRestockButton}
          onClick={() => setShowBulkRestock(!showBulkRestock)}
        >
          📦 {showBulkRestock ? 'Cancel' : 'Bulk Restock'}
        </button>
      </div>

      {/* Report Format Modal */}
      {showFormatModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3>Select Report Format</h3>
            <p>Choose how you want to export the ingredient status report.</p>
            <div className={styles.formRow}>
              <button
                type="button"
                className={styles.saveButton}
                onClick={() => {
                  setReportFormat("pdf");
                  setShowFormatModal(false);
                  handleGenerateIngredientStatusReport("pdf");
                }}
                disabled={generatingReport}
              >
                Generate PDF
              </button>
              <button
                type="button"
                className={styles.saveButton}
                onClick={() => {
                  setReportFormat("xls");
                  setShowFormatModal(false);
                  handleGenerateIngredientStatusReport("xls");
                }}
                disabled={generatingReport}
              >
                Generate XLS
              </button>
            </div>
            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={() => setShowFormatModal(false)}
                disabled={generatingReport}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Restock Controls */}
      {showBulkRestock && (
        <div className={styles.bulkRestockControls}>
          <div className={styles.bulkRestockHeader}>
            <h3>Bulk Restock Mode</h3>
            <div className={styles.bulkRestockActions}>
              <button
                className={styles.selectAllButton}
                onClick={selectAllLowStock}
                disabled={getLowStockItems().length === 0}
              >
                Select All Low Stock ({getLowStockItems().length})
              </button>
              <button
                className={styles.clearSelectionButton}
                onClick={clearSelection}
                disabled={selectedItems.size === 0}
              >
                Clear Selection
              </button>
              <button
                className={styles.bulkSubmitButton}
                onClick={handleBulkRestockSubmit}
                disabled={selectedItems.size === 0}
              >
                Restock Selected ({selectedItems.size})
              </button>
            </div>
          </div>
          {selectedItems.size > 0 && (
            <div className={styles.selectedItemsInfo}>
              Selected {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} for bulk restock
            </div>
          )}
        </div>
      )}

      {/* Edit Stock Modal */}
      {showEditStock && editingIngredient && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3>Update Stock - {editingIngredient.name}</h3>
            <form onSubmit={handleStockSubmit}>
              <div className={styles.formGroup}>
                <label htmlFor="currentStock">Current Stock:</label>
                <input
                  id="currentStock"
                  type="number"
                  min="0"
                  value={stockForm.currentStock}
                  readOnly
                />
              </div>
              <div className={styles.formGroup}>
                <label>New Total Stock (after purchase):</label>
                <input
                  type="number"
                  readOnly
                  value={packageQuantityBase !== null ? (editingIngredient.currentStock || 0) + packageQuantityBase : ""}
                  placeholder="Fill package fields to calculate"
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="minStock">Minimum Stock Level:</label>
                <input
                  id="minStock"
                  type="number"
                  min="0"
                  value={stockForm.minStock}
                  onChange={(e) => setStockForm({ ...stockForm, minStock: e.target.value })}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="unit">Unit:</label>
                <input
                  id="unit"
                  type="text"
                  placeholder="e.g., kg, lbs, units, boxes"
                  value={stockForm.unit}
                  onChange={(e) => setStockForm({ ...stockForm, unit: e.target.value })}
                  required
                />
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="purchaseDate">Purchase Date:</label>
                  <input
                    id="purchaseDate"
                    type="date"
                    value={purchaseDetails.date}
                    onChange={(e) => setPurchaseDetails({ ...purchaseDetails, date: e.target.value })}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="purchaseStore">Store Bought:</label>
                  <select
                    id="purchaseStore"
                    value={purchaseDetails.purchaseStoreId}
                    onChange={(e) => setPurchaseDetails({ ...purchaseDetails, purchaseStoreId: e.target.value })}
                    required
                  >
                    <option value="">Select a store</option>
                    {stores.map(store => (
                      <option key={store.id} value={store.id}>{store.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="packageCount">Packages Purchased (e.g., 5 bags):</label>
                  <input
                    id="packageCount"
                    type="number"
                    min="0"
                    step="any"
                    value={purchaseDetails.packageCount}
                    onChange={(e) => setPurchaseDetails({ ...purchaseDetails, packageCount: e.target.value })}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="packageSize">Package Size (e.g., 3):</label>
                  <input
                    id="packageSize"
                    type="number"
                    min="0"
                    step="any"
                    value={purchaseDetails.packageSize}
                    onChange={(e) => setPurchaseDetails({ ...purchaseDetails, packageSize: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="packageUnit">Package Unit (e.g., lbs):</label>
                  <select
                    id="packageUnit"
                    value={purchaseDetails.packageUnit}
                    onChange={(e) => setPurchaseDetails({ ...purchaseDetails, packageUnit: e.target.value })}
                  >
                    {STANDARD_UNITS.map(unit => (
                      <option key={unit} value={unit}>{unit}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="costPerPackage">Cost per Package (e.g., $12/bag):</label>
                  <input
                    id="costPerPackage"
                    type="number"
                    min="0"
                    step="any"
                    value={purchaseDetails.costPerPackage}
                    onChange={(e) => setPurchaseDetails({ ...purchaseDetails, costPerPackage: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="pricePerLb">Price per lb (optional override):</label>
                <input
                  id="pricePerLb"
                  type="number"
                  min="0"
                  step="any"
                  value={purchaseDetails.pricePerLb}
                  onChange={(e) => setPurchaseDetails({ ...purchaseDetails, pricePerLb: e.target.value })}
                  placeholder="Leave empty to auto-calc from packages"
                />
                <small>Used to compute totals if provided; otherwise derived from packages.</small>
              </div>
              <div className={styles.formGroup}>
                <label>Total Price:</label>
                <input
                  type="number"
                  readOnly
                  value={computedTotalPriceWithPackages !== null ? computedTotalPriceWithPackages : ""}
                  placeholder="Auto-calculated"
                />
                <small>
                  Calculated as packages × cost per package
                </small>
              </div>
              <div className={styles.formGroup}>
                <label>Derived Totals:</label>
                <div className={styles.derivedRow}>
                  <span>Total Quantity (base): {packageQuantityBase !== null ? packageQuantityBase : "-"} {editingIngredient.unit}</span>
                </div>
                <div className={styles.derivedRow}>
                  <span>Cost per base unit: {packageQuantityBase && computedTotalPriceWithPackages !== null ? (computedTotalPriceWithPackages / packageQuantityBase).toFixed(2) : "-"}</span>
                </div>
                <div className={styles.derivedRow}>
                  <span>Price per lb: {suggestedPricePerLb !== null ? suggestedPricePerLb.toFixed(2) : "-"}</span>
                </div>
              </div>
              <div className={styles.modalActions}>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditStock(false);
                    setEditingIngredient(null);
                    setStockForm({ currentStock: 0, minStock: 0, unit: "" });
                    resetPurchaseDetails();
                  }}
                  className={styles.cancelButton}
                >
                  Cancel
                </button>
                <button type="submit" className={styles.saveButton}>
                  Update Stock
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Inventory Grid */}
      <div className={styles.cardGrid}>
        {filteredIngredients.map(ingredient => {
          const status = getStockStatus(ingredient);
          const statusColor = getStatusColor(status);
          
          return (
            <div 
              key={ingredient.id} 
              id={`ingredient-${ingredient.id}`}
              className={`${styles.inventoryCard} ${highlightedId === ingredient.id ? styles.highlighted : ''}`}
            >
              {showBulkRestock && (
                <div className={styles.selectionCheckbox}>
                  <input
                    type="checkbox"
                    checked={selectedItems.has(ingredient.id)}
                    onChange={() => handleItemSelection(ingredient.id)}
                    disabled={isWaterName(ingredient.name)}
                    id={`select-${ingredient.id}`}
                  />
                  <label htmlFor={`select-${ingredient.id}`} />
                </div>
              )}
              <div className={styles.cardHeader}>
                <div className={styles.ingredientName}>{ingredient.name}</div>
                <div 
                  className={styles.statusBadge}
                  style={{ backgroundColor: statusColor }}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </div>
              </div>
              
              <div className={styles.cardContent}>
                <div className={styles.stockInfo}>
                  <div className={styles.stockCurrent}>
                    <span className={styles.stockLabel}>Current:</span>
                    <span className={styles.stockValue} style={{ color: statusColor }}>
                      {formatStockForDisplay(ingredient.currentStock)} {ingredient.unit}
                    </span>
                  </div>
                  <div className={styles.stockMin}>
                    <span className={styles.stockLabel}>Minimum:</span>
                    <span className={styles.stockValue}>
                      {ingredient.minStock} {ingredient.unit}
                    </span>
                  </div>
                </div>
                
                {showBulkRestock && selectedItems.has(ingredient.id) && (
                  <div className={styles.bulkRestockInput}>
                    <label htmlFor={`restock-${ingredient.id}`}>Add to stock:</label>
                    <div className={styles.restockCalculation}>
                      <input
                        id={`restock-${ingredient.id}`}
                        type="number"
                        min="0"
                        placeholder="Amount to add"
                        value={bulkRestockAmounts[ingredient.id] || ''}
                        onChange={(e) => handleBulkRestockAmountChange(ingredient.id, e.target.value)}
                      />
                      <span className={styles.restockPreview}>
                        → {formatStockForDisplay((ingredient.currentStock ?? 0) + (bulkRestockAmounts[ingredient.id] || 0))} {ingredient.unit}
                      </span>
                    </div>
                  </div>
                )}
                
                <div className={styles.ingredientStore}>
                  <span role="img" aria-label="store">🏬</span> {ingredient.storeName}
                </div>
                
                {ingredient.lastUpdated && (
                  <div className={styles.lastUpdated}>
                    Last updated: {new Date(ingredient.lastUpdated.seconds * 1000).toLocaleDateString()}
                  </div>
                )}
              </div>
              
              {!showBulkRestock && (
                <div className={styles.cardActions}>
                  <button
                    className={styles.updateButton}
                    onClick={() => handleStockUpdate(ingredient)}
                  >
                    Update Stock
                  </button>
                  <button
                    className={styles.editButton}
                    onClick={() => {
                      setEditIngredient(ingredient);
                      setForm({
                        name: ingredient.name,
                        store: ingredient.storeId,
                        currentStock: ingredient.currentStock,
                        minStock: ingredient.minStock,
                        unit: ingredient.unit,
                        pricePerLb: ingredient.pricePerLb ?? ""
                      });
                      setShowEdit(true);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className={styles.deleteButton}
                    onClick={() => deleteIngredient(ingredient.id)}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredIngredients.length === 0 && (
        <div className={styles.noResults}>
          {searchTerm ? `No ingredients found matching "${searchTerm}"` : "No ingredients found"}
        </div>
      )}

      {/* Add Ingredient Modal */}
      {showAdd && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3>Add New Ingredient</h3>
            <div className={styles.formGroup}>
              <label htmlFor="name">Ingredient Name:</label>
              <input
                id="name"
                type="text"
                placeholder="Enter ingredient name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="store">Store:</label>
              <div className={styles.storeSelectContainer}>
                <input
                  type="text"
                  placeholder="Search stores..."
                  value={storeSearch}
                  onChange={(e) => setStoreSearch(e.target.value)}
                  className={styles.storeSearchInput}
                />
                <select
                  id="store"
                  value={form.store}
                  onChange={(e) => setForm({ ...form, store: e.target.value })}
                  required
                  className={styles.storeSelect}
                >
                  <option value="">Select a store</option>
                  {stores
                    .filter(store => store.name.toLowerCase().includes(storeSearch.toLowerCase()))
                    .map(store => (
                      <option key={store.id} value={store.id}>
                        {store.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="currentStock">Current Stock:</label>
                <input
                  id="currentStock"
                  type="number"
                  min="0"
                  step="any"
                  value={form.currentStock}
                  onChange={(e) => setForm({ ...form, currentStock: e.target.value })}
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="minStock">Minimum Stock:</label>
                <input
                  id="minStock"
                  type="number"
                  min="0"
                  step="any"
                  value={form.minStock}
                  onChange={(e) => setForm({ ...form, minStock: e.target.value })}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="unit">Unit:</label>
              <select
                id="unit"
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                required
              >
                {STANDARD_UNITS.map(unit => (
                  <option key={unit} value={unit}>{unit}</option>
                ))}
              </select>
            </div>

            <div className={styles.modalActions}>
              <button
                type="button"
                onClick={() => {
                  setShowAdd(false);
                  setForm({ name: "", store: "", currentStock: 0, minStock: 5, unit: "units" });
                  setStoreSearch("");
                }}
                className={styles.cancelButton}
              >
                Cancel
              </button>
              <button onClick={addIngredient} className={styles.saveButton}>
                Add Ingredient
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Ingredient Modal */}
      {showEdit && editIngredient && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3>Edit Ingredient</h3>
            <div className={styles.formGroup}>
              <label htmlFor="editName">Ingredient Name:</label>
              <input
                id="editName"
                type="text"
                placeholder="Enter ingredient name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="editStore">Store:</label>
              <div className={styles.storeSelectContainer}>
                <input
                  type="text"
                  placeholder="Search stores..."
                  value={storeSearch}
                  onChange={(e) => setStoreSearch(e.target.value)}
                  className={styles.storeSearchInput}
                />
                <select
                  id="editStore"
                  value={form.store}
                  onChange={(e) => setForm({ ...form, store: e.target.value })}
                  required
                  className={styles.storeSelect}
                >
                  <option value="">Select a store</option>
                  {stores
                    .filter(store => store.name.toLowerCase().includes(storeSearch.toLowerCase()))
                    .map(store => (
                      <option key={store.id} value={store.id}>
                        {store.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="editCurrentStock">Current Stock:</label>
                <input
                  id="editCurrentStock"
                  type="number"
                  min="0"
                  step="any"
                  value={form.currentStock}
                  onChange={(e) => setForm({ ...form, currentStock: e.target.value })}
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="editMinStock">Minimum Stock:</label>
                <input
                  id="editMinStock"
                  type="number"
                  min="0"
                  step="any"
                  value={form.minStock}
                  onChange={(e) => setForm({ ...form, minStock: e.target.value })}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="editPricePerLb">Price per lb (optional):</label>
              <input
                id="editPricePerLb"
                type="number"
                min="0"
                step="any"
                value={form.pricePerLb}
                onChange={(e) => setForm({ ...form, pricePerLb: e.target.value })}
                placeholder="Set a default price/lb for this ingredient"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="editUnit">Unit:</label>
              <select
                id="editUnit"
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                required
              >
                {STANDARD_UNITS.map(unit => (
                  <option key={unit} value={unit}>{unit}</option>
                ))}
              </select>
            </div>

            <div className={styles.modalActions}>
              <button
                type="button"
                onClick={() => {
                  setShowEdit(false);
                  setEditIngredient(null);
                  setForm({ name: "", store: "", currentStock: 0, minStock: 5, unit: "units" });
                  setStoreSearch("");
                }}
                className={styles.cancelButton}
              >
                Cancel
              </button>
              <button onClick={updateIngredient} className={styles.saveButton}>
                Update Ingredient
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
